import { useState, useEffect } from 'react';
import { 
  Building2, Globe, Users, Award, CheckCircle2, ArrowRight, 
  Briefcase, Target, TrendingUp, Handshake, FileText, Shield,
  ChevronDown, ChevronUp, Upload, Loader2, Mail, Phone, MapPin,
  Linkedin, Instagram, Facebook, Twitter, Youtube, Clock, Star,
  DollarSign, Network, UserCheck, FileCheck, Send, AlertCircle
} from 'lucide-react';
import './StrategicPartnerAlliance.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StrategicPartnerAlliance() {
  const [formData, setFormData] = useState({
    company_name: '',
    website: '',
    year_established: '',
    registered_address: '',
    city: '',
    country: '',
    gst_number: '',
    contact_person: '',
    designation: '',
    email: '',
    mobile: '',
    company_size: '',
    technical_consultants: '',
    technology_expertise: '',
    industry_experience: '',
    partnership_model: '',
    company_profile_filename: '',
    company_profile_data: '',
    additional_comments: '',
    privacy_consent: false
  });
  
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    window.scrollTo(0, 0);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setFormStatus({ type: 'error', message: 'File size must be less than 10MB' });
        return;
      }
      if (file.type !== 'application/pdf') {
        setFormStatus({ type: 'error', message: 'Only PDF files are allowed' });
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          company_profile_filename: file.name,
          company_profile_data: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    if (!formData.privacy_consent) {
      setFormStatus({ type: 'error', message: 'Please accept the privacy policy to proceed.' });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/partner-application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        setFormStatus({ 
          type: 'success', 
          message: `Application submitted successfully! Your reference ID is: ${result.application_id}` 
        });
        setFormData({
          company_name: '', website: '', year_established: '', registered_address: '',
          city: '', country: '', gst_number: '', contact_person: '', designation: '',
          email: '', mobile: '', company_size: '', technical_consultants: '',
          technology_expertise: '', industry_experience: '', partnership_model: '',
          company_profile_filename: '', company_profile_data: '', additional_comments: '',
          privacy_consent: false
        });
      } else {
        setFormStatus({ type: 'error', message: result.detail || 'Failed to submit application' });
      }
    } catch (error) {
      setFormStatus({ type: 'error', message: 'Network error. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const partnerBenefits = [
    {
      icon: <Briefcase size={32} />,
      title: "Enterprise Business Opportunities",
      description: "Access eligible business requirements that Trempplin is authorized to share with qualified alliance partners."
    },
    {
      icon: <Users size={32} />,
      title: "IT Staffing Opportunities",
      description: "Participate in technology resource requirements based on relevant skills and availability."
    },
    {
      icon: <FileText size={32} />,
      title: "Joint Bidding & RFP Participation",
      description: "Collaborate with Trempplin on suitable enterprise proposals and implementation opportunities."
    },
    {
      icon: <Network size={32} />,
      title: "Technology Delivery Partnerships",
      description: "Explore subcontracting and delivery collaboration across enterprise technology projects."
    },
    {
      icon: <Handshake size={32} />,
      title: "Business Development Collaboration",
      description: "Participate in structured business development discussions and opportunity reviews."
    },
    {
      icon: <TrendingUp size={32} />,
      title: "Long-Term Strategic Growth",
      description: "Build ongoing partnerships based on technical capability, commercial competitiveness, and successful delivery."
    }
  ];

  const partnerTypes = [
    "IT services companies",
    "Technology startups",
    "IT staffing and recruitment companies",
    "Oracle Banking and FinTech service providers",
    "Cloud, Data, AI and cybersecurity companies",
    "Software development and implementation companies",
    "Enterprise consulting organizations"
  ];

  const membershipIncludes = [
    "12-month alliance membership",
    "Partner onboarding and capability evaluation",
    "Inclusion in the qualified Trempplin alliance ecosystem",
    "Consideration for eligible enterprise opportunities",
    "Opportunity briefings where applicable",
    "Joint proposal collaboration where mutually agreed",
    "Dedicated alliance relationship coordination",
    "Quarterly performance and partnership reviews"
  ];

  const processSteps = [
    { step: 1, title: "Apply", description: "Submit your application", icon: <FileText size={24} /> },
    { step: 2, title: "Capability Review", description: "We evaluate your profile", icon: <UserCheck size={24} /> },
    { step: 3, title: "Approval", description: "Application decision", icon: <CheckCircle2 size={24} /> },
    { step: 4, title: "Agreement", description: "Sign partnership terms", icon: <FileCheck size={24} /> },
    { step: 5, title: "Payment", description: "Membership fee payment", icon: <DollarSign size={24} /> },
    { step: 6, title: "Onboarding", description: "Partner orientation", icon: <Users size={24} /> },
    { step: 7, title: "Collaboration", description: "Business engagement", icon: <Handshake size={24} /> }
  ];

  const faqs = [
    {
      question: "Who can apply to become a Trempplin Strategic Partner?",
      answer: "IT services companies, technology startups, staffing firms, Oracle Banking specialists, cloud/AI/cybersecurity providers, software development companies, and enterprise consulting organizations with relevant experience and capabilities can apply."
    },
    {
      question: "What is the annual membership fee?",
      answer: "The annual membership fee is ₹5,00,000 (Five Lakhs) plus applicable GST. This provides 12 months of alliance membership with all included benefits."
    },
    {
      question: "Is business guaranteed with this membership?",
      answer: "No. Membership does not guarantee contracts, client introductions, minimum business requirements, revenue, or project awards. It provides access to the Trempplin alliance ecosystem and consideration for relevant opportunities."
    },
    {
      question: "What benefits does membership include?",
      answer: "Benefits include partner onboarding, inclusion in the qualified alliance ecosystem, consideration for eligible opportunities, opportunity briefings, joint proposal collaboration, dedicated relationship coordination, and quarterly partnership reviews."
    },
    {
      question: "Can partners participate in enterprise client opportunities?",
      answer: "Approved partners may be considered for relevant business requirements and opportunities that Trempplin is authorized to share. Participation depends on capability match, availability, and commercial alignment."
    },
    {
      question: "Does membership make us an official Oracle, TCS, Cognizant, or Sonata partner?",
      answer: "No. This is an independent Trempplin alliance program. It does not constitute official partnership, vendor registration, or certification with any third-party company including Oracle, TCS, Cognizant, Sonata, or any of their clients."
    },
    {
      question: "How does the onboarding process work?",
      answer: "After approval and agreement signing, you'll receive an invoice for the membership fee. Upon payment, you'll be onboarded with a dedicated alliance coordinator who will guide you through the partner orientation process."
    },
    {
      question: "When is payment required?",
      answer: "Payment is required only after your application is approved, the partnership agreement is signed, and an official invoice is issued. We do not collect payments during the application process."
    },
    {
      question: "How does renewal work?",
      answer: "Membership is valid for 12 months from activation. Before expiry, you'll receive renewal information. Renewal is subject to partnership performance review and mutual agreement."
    },
    {
      question: "What are the project-specific commercial terms?",
      answer: "Project-specific commercials, subcontracting rates, and delivery terms are agreed separately for each opportunity based on scope, complexity, and market conditions."
    }
  ];

  return (
    <div className="spa-page">
      {/* Navigation */}
      <nav className={`spa-nav ${isScrolled ? 'scrolled' : ''}`}>
        <div className="spa-nav-container">
          <a href="/" className="spa-nav-logo">
            <img src="https://customer-assets-cm19k8pv.emergentagent.net/job_tremplin-hub/artifacts/xv6bvpuk_1.png" alt="Trempplin" />
          </a>
          <div className="spa-nav-links">
            <a href="/">Home</a>
            <button onClick={() => scrollToSection('benefits')}>Benefits</button>
            <button onClick={() => scrollToSection('pricing')}>Pricing</button>
            <button onClick={() => scrollToSection('apply')}>Apply Now</button>
            <button onClick={() => scrollToSection('faq')}>FAQ</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="spa-hero">
        <div className="spa-hero-bg">
          <div className="spa-hero-gradient"></div>
          <div className="spa-hero-pattern"></div>
        </div>
        <div className="spa-hero-content">
          <div className="spa-hero-badge">
            <Star size={16} />
            <span>Founding Partner Cohort 2026-27</span>
          </div>
          <h1>TREMPPLIN STRATEGIC PARTNER ALLIANCE PROGRAM</h1>
          <p className="spa-hero-tagline">Your Capabilities. Our Ecosystem. Shared Growth.</p>
          <p className="spa-hero-subtitle">Grow Beyond Your Network. Partner with Trempplin.</p>
          <p className="spa-hero-description">
            Accelerate your IT business growth by joining Trempplin's enterprise technology and delivery collaboration ecosystem. 
            Connect with opportunities across IT services, banking technology, consulting, implementation, technical staffing, and enterprise transformation.
          </p>
          
          <div className="spa-hero-pricing">
            <div className="spa-price-box">
              <span className="spa-price-label">Annual Membership Fee</span>
              <span className="spa-price-amount">₹5,00,000</span>
              <span className="spa-price-gst">+ Applicable GST</span>
            </div>
            <div className="spa-hero-meta">
              <div className="spa-meta-item">
                <Clock size={18} />
                <span>12 Months Validity</span>
              </div>
              <div className="spa-meta-item">
                <Target size={18} />
                <span>10 Qualified Companies</span>
              </div>
            </div>
          </div>
          
          <div className="spa-hero-cta">
            <button onClick={() => scrollToSection('apply')} className="spa-btn spa-btn-primary">
              Apply to Become a Partner <ArrowRight size={20} />
            </button>
            <button onClick={() => scrollToSection('benefits')} className="spa-btn spa-btn-secondary">
              Explore Partnership Benefits
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="spa-section spa-about">
        <div className="spa-container">
          <div className="spa-section-header">
            <span className="spa-overline">About the Program</span>
            <h2>Strategic Collaboration for Mutual Growth</h2>
          </div>
          <div className="spa-about-content">
            <div className="spa-about-text">
              <p>
                Trempplin has built relationships and experience across enterprise technology, Oracle Banking solutions, 
                IT consulting, implementation services, technical staffing, and corporate training.
              </p>
              <p>
                Through this alliance program, Trempplin aims to collaborate with qualified IT companies, startups, 
                technology consulting firms, and staffing organizations.
              </p>
              <p>
                Approved partners can be considered for relevant business requirements and joint delivery opportunities 
                through Trempplin's professional ecosystem. The program is designed to create mutual growth through 
                business development collaboration, technical capabilities, and successful project execution.
              </p>
            </div>
            <div className="spa-about-stats">
              <div className="spa-stat-card">
                <Globe size={32} />
                <span className="spa-stat-value">45+</span>
                <span className="spa-stat-label">Countries Served</span>
              </div>
              <div className="spa-stat-card">
                <Building2 size={32} />
                <span className="spa-stat-value">70+</span>
                <span className="spa-stat-label">Banking Clients</span>
              </div>
              <div className="spa-stat-card">
                <Award size={32} />
                <span className="spa-stat-value">100+</span>
                <span className="spa-stat-label">Projects Delivered</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="spa-section spa-benefits">
        <div className="spa-container">
          <div className="spa-section-header">
            <span className="spa-overline">Partner Benefits</span>
            <h2>What You Gain as a Strategic Partner</h2>
          </div>
          <div className="spa-benefits-grid">
            {partnerBenefits.map((benefit, index) => (
              <div key={index} className="spa-benefit-card">
                <div className="spa-benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Can Apply Section */}
      <section className="spa-section spa-eligibility">
        <div className="spa-container">
          <div className="spa-section-header">
            <span className="spa-overline">Eligibility</span>
            <h2>Who Can Become a Partner?</h2>
          </div>
          <div className="spa-eligibility-grid">
            {partnerTypes.map((type, index) => (
              <div key={index} className="spa-eligibility-item">
                <CheckCircle2 size={24} />
                <span>{type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="spa-section spa-pricing">
        <div className="spa-container">
          <div className="spa-section-header">
            <span className="spa-overline">Investment</span>
            <h2>Annual Membership Pricing</h2>
          </div>
          <div className="spa-pricing-card">
            <div className="spa-pricing-header">
              <span className="spa-pricing-badge">STRATEGIC ALLIANCE MEMBERSHIP</span>
              <div className="spa-pricing-amount">
                <span className="spa-currency">₹</span>
                <span className="spa-amount">5,00,000</span>
                <span className="spa-period">/ Year</span>
              </div>
              <span className="spa-pricing-gst">+ Applicable GST</span>
            </div>
            <div className="spa-pricing-body">
              <h4>Membership Includes:</h4>
              <ul className="spa-pricing-features">
                {membershipIncludes.map((item, index) => (
                  <li key={index}>
                    <CheckCircle2 size={18} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="spa-pricing-disclaimer">
                <AlertCircle size={18} />
                <p>
                  <strong>Important:</strong> Membership does not guarantee contracts, client introductions, 
                  minimum business requirements, revenue, or project awards. Project-specific commercials 
                  and subcontracting rates will be agreed separately.
                </p>
              </div>
            </div>
            <div className="spa-pricing-footer">
              <button onClick={() => scrollToSection('apply')} className="spa-btn spa-btn-primary spa-btn-full">
                Apply for Membership <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="spa-section spa-process">
        <div className="spa-container">
          <div className="spa-section-header">
            <span className="spa-overline">How It Works</span>
            <h2>Application & Onboarding Process</h2>
          </div>
          <div className="spa-process-timeline">
            {processSteps.map((step, index) => (
              <div key={index} className="spa-process-step">
                <div className="spa-step-icon">{step.icon}</div>
                <div className="spa-step-content">
                  <span className="spa-step-number">Step {step.step}</span>
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
                {index < processSteps.length - 1 && <div className="spa-step-connector"></div>}
              </div>
            ))}
          </div>
          <div className="spa-process-note">
            <Shield size={20} />
            <p>
              Membership payments are not collected automatically. Payment will be requested only after 
              company approval, agreement signing, and official invoice issuance.
            </p>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section id="apply" className="spa-section spa-apply">
        <div className="spa-container">
          <div className="spa-section-header">
            <span className="spa-overline">Join Us</span>
            <h2>Partner Registration Form</h2>
            <p>Complete the form below to apply for the Trempplin Strategic Partner Alliance Program</p>
          </div>
          
          {formStatus.message && (
            <div className={`spa-form-status ${formStatus.type}`}>
              {formStatus.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
              <span>{formStatus.message}</span>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="spa-application-form">
            <div className="spa-form-section">
              <h3>Company Information</h3>
              <div className="spa-form-grid">
                <div className="spa-form-group">
                  <label>Company Name *</label>
                  <input 
                    type="text" 
                    name="company_name" 
                    value={formData.company_name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter company name"
                  />
                </div>
                <div className="spa-form-group">
                  <label>Website</label>
                  <input 
                    type="url" 
                    name="website" 
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://www.example.com"
                  />
                </div>
                <div className="spa-form-group">
                  <label>Year of Establishment</label>
                  <input 
                    type="text" 
                    name="year_established" 
                    value={formData.year_established}
                    onChange={handleInputChange}
                    placeholder="e.g., 2015"
                  />
                </div>
                <div className="spa-form-group">
                  <label>GST Number (if applicable)</label>
                  <input 
                    type="text" 
                    name="gst_number" 
                    value={formData.gst_number}
                    onChange={handleInputChange}
                    placeholder="Enter GST number"
                  />
                </div>
                <div className="spa-form-group spa-form-full">
                  <label>Registered Office Address *</label>
                  <input 
                    type="text" 
                    name="registered_address" 
                    value={formData.registered_address}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter complete address"
                  />
                </div>
                <div className="spa-form-group">
                  <label>City *</label>
                  <input 
                    type="text" 
                    name="city" 
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter city"
                  />
                </div>
                <div className="spa-form-group">
                  <label>Country *</label>
                  <input 
                    type="text" 
                    name="country" 
                    value={formData.country}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter country"
                  />
                </div>
              </div>
            </div>

            <div className="spa-form-section">
              <h3>Contact Person Details</h3>
              <div className="spa-form-grid">
                <div className="spa-form-group">
                  <label>Contact Person Name *</label>
                  <input 
                    type="text" 
                    name="contact_person" 
                    value={formData.contact_person}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter full name"
                  />
                </div>
                <div className="spa-form-group">
                  <label>Designation *</label>
                  <input 
                    type="text" 
                    name="designation" 
                    value={formData.designation}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., CEO, Director"
                  />
                </div>
                <div className="spa-form-group">
                  <label>Official Email *</label>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="name@company.com"
                  />
                </div>
                <div className="spa-form-group">
                  <label>Mobile Number *</label>
                  <input 
                    type="tel" 
                    name="mobile" 
                    value={formData.mobile}
                    onChange={handleInputChange}
                    required
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
              </div>
            </div>

            <div className="spa-form-section">
              <h3>Company Profile</h3>
              <div className="spa-form-grid">
                <div className="spa-form-group">
                  <label>Company Size *</label>
                  <select 
                    name="company_size" 
                    value={formData.company_size}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select company size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501-1000">501-1000 employees</option>
                    <option value="1000+">1000+ employees</option>
                  </select>
                </div>
                <div className="spa-form-group">
                  <label>Number of Technical Consultants</label>
                  <input 
                    type="text" 
                    name="technical_consultants" 
                    value={formData.technical_consultants}
                    onChange={handleInputChange}
                    placeholder="e.g., 50"
                  />
                </div>
                <div className="spa-form-group spa-form-full">
                  <label>Technology Expertise *</label>
                  <textarea 
                    name="technology_expertise" 
                    value={formData.technology_expertise}
                    onChange={handleInputChange}
                    required
                    rows="3"
                    placeholder="e.g., Oracle FLEXCUBE, Java, Cloud, AI/ML, etc."
                  ></textarea>
                </div>
                <div className="spa-form-group spa-form-full">
                  <label>Industry Experience *</label>
                  <textarea 
                    name="industry_experience" 
                    value={formData.industry_experience}
                    onChange={handleInputChange}
                    required
                    rows="3"
                    placeholder="e.g., Banking, Financial Services, Insurance, etc."
                  ></textarea>
                </div>
                <div className="spa-form-group">
                  <label>Preferred Partnership Model *</label>
                  <select 
                    name="partnership_model" 
                    value={formData.partnership_model}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select partnership model</option>
                    <option value="staffing">IT Staffing & Resources</option>
                    <option value="subcontracting">Subcontracting & Delivery</option>
                    <option value="joint-bidding">Joint Bidding & Proposals</option>
                    <option value="reseller">Technology Reseller</option>
                    <option value="multiple">Multiple Models</option>
                  </select>
                </div>
                <div className="spa-form-group">
                  <label>Company Profile (PDF, max 10MB)</label>
                  <div className="spa-file-upload">
                    <input 
                      type="file" 
                      id="company_profile"
                      accept=".pdf"
                      onChange={handleFileChange}
                    />
                    <label htmlFor="company_profile" className="spa-file-label">
                      <Upload size={20} />
                      <span>{formData.company_profile_filename || 'Choose PDF file'}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="spa-form-section">
              <h3>Additional Information</h3>
              <div className="spa-form-group spa-form-full">
                <label>Additional Comments</label>
                <textarea 
                  name="additional_comments" 
                  value={formData.additional_comments}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Any additional information you'd like to share..."
                ></textarea>
              </div>
            </div>

            <div className="spa-form-consent">
              <label className="spa-checkbox-label">
                <input 
                  type="checkbox" 
                  name="privacy_consent"
                  checked={formData.privacy_consent}
                  onChange={handleInputChange}
                  required
                />
                <span className="spa-checkbox-custom"></span>
                <span>
                  I agree to the <a href="/privacy-policy" target="_blank">Privacy Policy</a> and 
                  <a href="/partner-terms" target="_blank"> Partner Program Terms</a>. I understand that 
                  membership does not guarantee business or official third-party vendor status.
                </span>
              </label>
            </div>

            <button type="submit" className="spa-btn spa-btn-primary spa-btn-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 size={20} className="spa-spinner" />
                  Submitting Application...
                </>
              ) : (
                <>
                  Submit Partnership Application <Send size={20} />
                </>
              )}
            </button>
          </form>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="spa-section spa-faq">
        <div className="spa-container">
          <div className="spa-section-header">
            <span className="spa-overline">FAQ</span>
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="spa-faq-list">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className={`spa-faq-item ${expandedFaq === index ? 'expanded' : ''}`}
              >
                <button 
                  className="spa-faq-question"
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  {expandedFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                <div className="spa-faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="spa-section spa-cta">
        <div className="spa-container">
          <div className="spa-cta-content">
            <h2>Ready to Partner with Trempplin?</h2>
            <p>Join our founding cohort of strategic partners and unlock new growth opportunities.</p>
            <button onClick={() => scrollToSection('apply')} className="spa-btn spa-btn-gold">
              Apply Now <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="spa-footer">
        <div className="spa-container">
          <div className="spa-footer-content">
            <div className="spa-footer-brand">
              <img src="https://customer-assets-cm19k8pv.emergentagent.net/job_tremplin-hub/artifacts/xv6bvpuk_1.png" alt="Trempplin" />
              <p>Global Banking Technology & FinTech Company</p>
            </div>
            <div className="spa-footer-links">
              <a href="/">Home</a>
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/partner-terms">Partner Terms</a>
              <a href="mailto:contactus@trempplin.com">Contact Us</a>
            </div>
            <div className="spa-footer-social">
              <a href="https://www.linkedin.com/company/trempplin/" target="_blank" rel="noopener noreferrer"><Linkedin size={20} /></a>
              <a href="https://www.instagram.com/trempplin/" target="_blank" rel="noopener noreferrer"><Instagram size={20} /></a>
              <a href="https://www.facebook.com/trempplin" target="_blank" rel="noopener noreferrer"><Facebook size={20} /></a>
              <a href="https://twitter.com/trempplin" target="_blank" rel="noopener noreferrer"><Twitter size={20} /></a>
            </div>
          </div>
          <div className="spa-footer-bottom">
            <p>&copy; {new Date().getFullYear()} Trempplin Technologies Pvt. Ltd. All rights reserved.</p>
            <p className="spa-disclaimer">
              This is an independent Trempplin alliance program. It does not constitute official partnership 
              with Oracle, TCS, Cognizant, Sonata, or any third-party company.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default StrategicPartnerAlliance;
